/**
 * The one place the browser talks to @acs/api.
 *
 * Everything above this file (hooks, components) deals in typed DTOs from
 * `@acs/shared` and never touches `fetch`, a URL string, or a status code
 * directly. That keeps three things centralised: query-string encoding
 * (arrays must repeat, empties must vanish), the 15s ceiling on any single
 * request, and the error shape the retry policy in `query-client.ts` reads.
 */

/**
 * Empty string means same-origin, which is what we want in dev: Vite proxies
 * `/api` to the API process, so the browser never makes a cross-origin
 * request and cookies just work. In a split deployment this is set to the
 * absolute API origin at build time.
 */
const RAW_API_BASE: unknown = import.meta.env.VITE_API_URL
export const API_BASE: string =
  typeof RAW_API_BASE === 'string' ? RAW_API_BASE.replace(/\/+$/, '') : ''

/**
 * No console request is allowed to hang a tab. Aggregates are the slow ones.
 *
 * The DEFAULT, not a ceiling: a handful of endpoints do real work behind the
 * request — opening an IMAP session to read a verification code, for one — and
 * their own server-side budget is longer than this. Those pass `timeoutMs`
 * explicitly, and the rule for choosing it is in the note on `request`.
 */
const REQUEST_TIMEOUT_MS = 15_000

/**
 * Thrown for any non-2xx response, and for transport failures with
 * `status === 0`. The distinction matters: `query-client.ts` retries
 * `status === 0` (the API is restarting) but never a 4xx (the request itself
 * is wrong, so replaying it just burns quota).
 */
export class ApiError extends Error {
  readonly status: number
  readonly body?: unknown

  constructor(message: string, status: number, body?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

/**
 * The API's machine code for a failure, or null.
 *
 * The envelope is `{ error: { code, message } }`; `code` is the stable half of
 * the contract and `message` is prose. A caller that wants to DO something
 * specific about a particular refusal — open a dialog for `gender_required`
 * rather than toast it — switches on this, never on the message text.
 */
export function apiErrorCode(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null
  const body = err.body
  if (!body || typeof body !== 'object') return null
  const envelope = (body as Record<string, unknown>)['error']
  if (!envelope || typeof envelope !== 'object') return null
  const code = (envelope as Record<string, unknown>)['code']
  return typeof code === 'string' && code !== '' ? code : null
}

/* ------------------------------------------------------------------ *
 * Query string
 * ------------------------------------------------------------------ */

function isUsable(v: unknown): boolean {
  return v !== undefined && v !== null && v !== ''
}

/**
 * Array values repeat the key (`?mode=0&mode=1`) because that is what the
 * API's repeatable-param parser expects; `undefined`, `null` and `''` are
 * dropped entirely so a cleared filter input does not turn into `q=` and
 * defeat the server's "no filter" fast path.
 */
function encodeParams(params: Record<string, unknown> | undefined): string {
  if (!params) return ''
  const sp = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      for (const item of value as unknown[]) {
        if (isUsable(item)) sp.append(key, String(item))
      }
    } else if (isUsable(value)) {
      sp.append(key, String(value))
    }
  }
  return sp.toString()
}

function buildUrl(path: string, params?: Record<string, unknown>): string {
  const suffix = path.startsWith('/') ? path : `/${path}`
  const qs = encodeParams(params)
  return qs ? `${API_BASE}${suffix}?${qs}` : `${API_BASE}${suffix}`
}

/* ------------------------------------------------------------------ *
 * Response handling
 * ------------------------------------------------------------------ */

/** Reads the body once, tolerating empty (204) and non-JSON error pages. */
async function readBody(res: Response): Promise<unknown> {
  const text = await res.text()
  if (text === '') return undefined
  const contentType = res.headers.get('content-type') ?? ''
  if (contentType.includes('json')) {
    try {
      return JSON.parse(text) as unknown
    } catch {
      return text
    }
  }
  return text
}

/**
 * Turn an error body into the sentence the user reads.
 *
 * FAILURE MODE FIXED: the API's envelope is `{ error: { code, message } }` — a
 * nested *object*. The previous version only accepted a string at `error` or
 * `message`, so `rec['error']` was an object, the type guard rejected it, and
 * every single API failure fell through to the generic
 * `500 Internal Server Error — GET /api/…`. The real reason the server gave
 * ("Too many live connections from this client.") was already in the body and
 * was thrown away. Unwrap `error.message` first, then fall back to Fastify's
 * flat built-in shape (`{ error: 'Not Found', message: '…' }`), where `message`
 * is the specific one and `error` is only the status name.
 */
function errorMessage(payload: unknown, res: Response, method: string, path: string): string {
  if (payload && typeof payload === 'object') {
    const rec = payload as Record<string, unknown>
    const envelope = rec['error']

    if (envelope && typeof envelope === 'object') {
      const nested = (envelope as Record<string, unknown>)['message']
      if (typeof nested === 'string' && nested !== '') return nested
    }

    const flat = rec['message']
    if (typeof flat === 'string' && flat !== '') return flat
    if (typeof envelope === 'string' && envelope !== '') return envelope
  }
  if (typeof payload === 'string' && payload !== '' && payload.length < 300) return payload
  return `${res.status} ${res.statusText || 'Request failed'} — ${method} ${path}`
}

/**
 * @param opts.timeoutMs Overrides the default ceiling for this one call.
 *
 * Set it LONGER than the server's own budget for the endpoint, never shorter.
 * A client that gives up first reports "could not reach the server" for work
 * that was in fact completing — and for anything with a side effect, that is
 * what makes an operator press the button a second time.
 */
async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  opts: {
    params?: Record<string, unknown>
    body?: unknown
    signal?: AbortSignal
    timeoutMs?: number
  }
): Promise<T> {
  const timeoutMs = opts.timeoutMs ?? REQUEST_TIMEOUT_MS
  // Two independent reasons to give up: the caller unmounted (React Query
  // passes its own signal) or the request blew the 15s ceiling. `any` folds
  // them into one signal so neither leaks a pending fetch.
  //
  // Both statics are feature-detected: `AbortSignal.timeout` is Safari 16+,
  // `AbortSignal.any` is Safari 17.4+, and this line runs BEFORE the try below
  // — on an older iPad a bare call here escapes as a raw TypeError that the
  // catch never converts, so every request (login included) dies with a
  // misleading "could not reach the server". Degradation on those engines:
  // the caller's signal wins, and without one the 15s ceiling is simply lost.
  const timeoutSignal =
    typeof AbortSignal.timeout === 'function' ? AbortSignal.timeout(timeoutMs) : undefined
  const signal =
    opts.signal && timeoutSignal && typeof AbortSignal.any === 'function'
      ? AbortSignal.any([opts.signal, timeoutSignal])
      : (opts.signal ?? timeoutSignal)

  const init: RequestInit = {
    method,
    signal,
    // Session cookie rides along; harmless same-origin, required when the API
    // is deployed on its own host.
    credentials: 'include',
    headers:
      opts.body === undefined
        ? { accept: 'application/json' }
        : { accept: 'application/json', 'content-type': 'application/json' },
  }
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body)

  let res: Response
  try {
    res = await fetch(buildUrl(path, opts.params), init)
  } catch (err) {
    // A caller-initiated abort must propagate untouched — React Query
    // recognises it as a cancellation and does not surface it as an error.
    if (opts.signal?.aborted) throw err
    if (timeoutSignal?.aborted) {
      throw new ApiError(`Timed out after ${timeoutMs}ms — ${method} ${path}`, 0)
    }
    const detail = err instanceof Error ? err.message : String(err)
    throw new ApiError(`Network error — ${method} ${path}: ${detail}`, 0)
  }

  const payload = await readBody(res)
  if (!res.ok) throw new ApiError(errorMessage(payload, res, method, path), res.status, payload)
  return payload as T
}

/* ------------------------------------------------------------------ *
 * Public surface
 * ------------------------------------------------------------------ */

export function apiGet<T>(
  path: string,
  params?: Record<string, unknown>,
  signal?: AbortSignal
): Promise<T> {
  return request<T>('GET', path, { params, signal })
}

export function apiPost<T>(
  path: string,
  body?: unknown,
  signal?: AbortSignal,
  /** Only for endpoints whose server-side budget exceeds the default — see `request`. */
  timeoutMs?: number
): Promise<T> {
  return request<T>('POST', path, { body, signal, timeoutMs })
}

/**
 * PUT, for endpoints that replace a value rather than append one.
 *
 * Distinct from POST because the API distinguishes them: settings are idempotent
 * replacements, so a retried PUT is harmless, while a retried POST on this API
 * may not be.
 */
export function apiPut<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  return request<T>('PUT', path, { body, signal })
}

/**
 * PATCH, for a partial update.
 *
 * Distinct from PUT because the API distinguishes them: a customer's three
 * write-only fields are never sent back to the client, so a PUT that replaced
 * the whole document would blank them on every save. Only what is sent is
 * written.
 */
export function apiPatch<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  return request<T>('PATCH', path, { body, signal })
}

export function apiDelete<T>(path: string, signal?: AbortSignal): Promise<T> {
  return request<T>('DELETE', path, { signal })
}
